import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * RTK Query slice for the Owner Report (REP-205, Listing/Push pillar — the
 * "REPORT IT" surface).
 *
 * The report is fully DERIVED on read by the backend
 * (`listingReport.service.ts`): the inbound union (derived pursuits over the
 * shared Property + stored ListingInterest rows) collapsed to AGGREGATE
 * COUNTS — the owner never sees pursuer identities / PII — plus
 * Activity-stream metrics scoped to the listing's property and
 * disposition-goal chips read live off the ListingSheet.
 *
 * `getReport` is a pure preview (no writes). `sendReport` persists a
 * ListingReport snapshot (payloadJson + brokerNote + sentAt) and delivers a
 * broker-reviewed email to the owner via the existing transactional mail
 * path.
 *
 * Backend routes (mounted under `/listings`, FE `/api` stripped upstream):
 *   GET  /api/listings/:id/report        — compute / preview
 *   POST /api/listings/:id/report/send   — persist snapshot + email owner
 *
 * Keep the types below in sync with `src/dtos/listingReport.dto.ts`.
 */

// ─── Payload mirrors ───────────────────────────────────────────────────
// Keep in sync with src/dtos/listingReport.dto.ts on the backend.

/** A disposition-goal chip read off the ListingSheet. */
export interface ReportGoalChip {
  key:
    | "askingPrice"
    | "askingLeaseRatePerSfPerYr"
    | "targetCapRate"
    | "desiredTimeline"
    | "keyDate";
  label: string;
  value: string;
}

/**
 * A "this period" metric tile. The backend OMITS the `views` tile entirely
 * when there's no first-party view data (we never fabricate a zero), so the
 * FE renders whatever tiles it receives and gracefully shows nothing for
 * Views when absent.
 */
export interface ReportTile {
  key: "tours" | "lois" | "interestedParties" | "views";
  label: string;
  value: number;
}

/**
 * Cadence nudge data (opinionated-but-not-blocking). The FE renders an
 * orange nudge when `overdue` — it NEVER blocks a send.
 */
export interface ReportCadence {
  preferredUpdateFrequency: string | null;
  lastSentAt: string | null;
  /** Whole days since the last send (null if never sent). */
  daysSinceLastSent: number | null;
  /** True when daysSinceLastSent exceeds the preferred frequency window. */
  overdue: boolean;
}

/**
 * A tour/event note surfaced in the report's "Conversations" section
 * (REP-206 §6). Broker-internal — sourced from EventNote on events tied to
 * the listing's property, NOT sent to the owner.
 */
export interface ReportConversation {
  id: string;
  text: string;
  eventId: string;
  eventName: string | null;
  authorName: string | null;
  createdAt: string;
}

/** The fully-computed owner report payload (aggregate counts only). */
export interface ListingReportPayload {
  listingProjectId: string;
  propertyId: string;
  propertyName: string | null;
  periodStart: string;
  periodEnd: string;
  goals: ReportGoalChip[];
  tiles: ReportTile[];
  cadence: ReportCadence;
  /** Tour/event notes tied to this listing's property (broker-internal). */
  conversations: ReportConversation[];
  brokerNote: string | null;
  /** ISO of when this snapshot was last sent (null on a fresh preview). */
  lastSentAt: string | null;
}

/** POST body for sending an owner report. */
export interface SendListingReportBody {
  listingProjectId: string;
  brokerNote?: string;
  periodStart?: string;
  periodEnd?: string;
}

export interface SendListingReportResult {
  report: {
    id: string;
    listingProjectId: string;
    periodStart: string;
    periodEnd: string;
    brokerNote: string | null;
    sentAt: string | null;
  };
  payload: ListingReportPayload;
}

export const listingReportApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "listingReportApi",
    baseUrl: "/api/listings",
  }),
  reducerPath: "listingReportApi",
  tagTypes: ["ListingReport"],
  endpoints: (builder) => ({
    /**
     * GET /api/listings/:id/report
     * Fully-derived preview of the owner report — no writes.
     */
    getListingReport: builder.query<{ data: ListingReportPayload }, string>({
      query: (listingProjectId) => `/${listingProjectId}/report`,
      providesTags: (_result, _error, id) => [{ type: "ListingReport", id }],
    }),

    /**
     * POST /api/listings/:id/report/send
     * Persists the snapshot + emails the owner. Invalidates the report so
     * the cadence nudge (last sent) refreshes immediately.
     */
    sendListingReport: builder.mutation<
      { data: SendListingReportResult },
      SendListingReportBody
    >({
      query: ({ listingProjectId, ...body }) => ({
        url: `/${listingProjectId}/report/send`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { listingProjectId }) => [
        { type: "ListingReport", id: listingProjectId },
      ],
    }),
  }),
});

export const { useGetListingReportQuery, useSendListingReportMutation } =
  listingReportApi;
